import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./src/contexts/AuthContext";
import { RenderQueueProvider } from "./src/contexts/RenderQueueContext";
import { ErrorBoundary } from "./src/components/ErrorBoundary";
import Layout from "./src/components/Layout";
const Home = lazy(() => import("./src/pages/Home"));
const Generator = lazy(() => import("./src/pages/Generator"));
const Apps = lazy(() => import("./src/pages/Apps"));
const GuidedApp = lazy(() => import("./src/pages/GuidedApp"));
const Community = lazy(() => import("./src/pages/Community"));
const Share = lazy(() => import("./src/pages/Share"));
const PublicProfile = lazy(() => import("./src/pages/PublicProfile"));
const Auth = lazy(() => import("./src/pages/Auth"));
const History = lazy(() => import("./src/pages/History"));
const Profile = lazy(() => import("./src/pages/Profile"));
const Admin = lazy(() => import("./src/pages/Admin"));
const NotFound = lazy(() => import("./src/pages/NotFound"));
function Protected({
  children,
  admin = false,
}: {
  children: React.ReactNode;
  admin?: boolean;
}) {
  const { user, loading } = useAuth(),
    location = useLocation();
  if (loading)
    return (
      <main className="page container">
        <div className="skeleton" />
      </main>
    );
  if (!user)
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}
export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <RenderQueueProvider>
            <Suspense
              fallback={
                <main className="page container">
                  <div className="skeleton" />
                </main>
              }
            >
              <Routes>
                <Route element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route
                    path="image-generator"
                    element={<Generator kind="image" />}
                  />
                  <Route
                    path="video-generator"
                    element={<Generator kind="video" />}
                  />
                  <Route path="apps" element={<Apps />} />
                  <Route path="apps/:appId" element={<GuidedApp />} />
                  <Route path="community" element={<Community />} />
                  <Route path="share/:id" element={<Share />} />
                  <Route path="u/:username" element={<PublicProfile />} />
                  <Route path="login" element={<Auth mode="login" />} />
                  <Route path="signup" element={<Auth mode="signup" />} />
                  <Route
                    path="history"
                    element={
                      <Protected>
                        <History />
                      </Protected>
                    }
                  />
                  <Route
                    path="profile"
                    element={
                      <Protected>
                        <Profile />
                      </Protected>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <Protected>
                        <Profile />
                      </Protected>
                    }
                  />
                  <Route
                    path="admin"
                    element={
                      <Protected admin>
                        <Admin />
                      </Protected>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Suspense>
          </RenderQueueProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
